export interface AppConfig {
  /** Origin of tendly-api without a trailing slash. */
  readonly apiBaseUrl: string
  /** GlitchTip DSN, or null when error reporting is off. */
  readonly glitchtipDsn: string | null
  /** Build identifier; tags every GlitchTip event and names the release. */
  readonly appVersion: string
  /** The Sockudo app for live updates, or null when the build has none. */
  readonly liveUpdates: LiveUpdatesConfig | null
}

export interface LiveUpdatesConfig {
  /** The public app key of the Sockudo app (never the secret). */
  readonly key: string
  /** Host name of the Sockudo server, without scheme or port. */
  readonly host: string
}

export class ConfigError extends Error {
  override readonly name = 'ConfigError'
}

/** The variables this application reads (declared in env.d.ts). */
export type AppEnv = Pick<
  ImportMetaEnv,
  'VITE_API_BASE_URL' | 'VITE_GLITCHTIP_DSN' | 'VITE_APP_VERSION' | 'VITE_WS_KEY' | 'VITE_WS_HOST'
>

/** The trimmed value of a variable, or null when it is unset or blank. */
function readVar(env: AppEnv, key: keyof AppEnv): string | null {
  const value = env[key]?.trim() ?? ''
  return value === '' ? null : value
}

/** The trimmed value of a variable that every build must set. */
function requireVar(env: AppEnv, key: keyof AppEnv): string {
  const value = readVar(env, key)
  if (value === null) {
    throw new ConfigError(`${key} is not set. Copy .env.example to .env and set it.`)
  }
  return value
}

function readApiBaseUrl(env: AppEnv): string {
  const value = requireVar(env, 'VITE_API_BASE_URL')
  if (!/^https?:\/\//.test(value)) {
    throw new ConfigError('VITE_API_BASE_URL must be an http(s) origin.')
  }
  if (value.endsWith('/')) {
    throw new ConfigError('VITE_API_BASE_URL must not end with a slash.')
  }
  return value
}

/**
 * Both Sockudo variables, or neither: one without the other is a broken build,
 * not a build without live updates.
 */
function readLiveUpdates(env: AppEnv): LiveUpdatesConfig | null {
  const key = readVar(env, 'VITE_WS_KEY')
  const host = readVar(env, 'VITE_WS_HOST')
  if (key === null && host === null) {
    return null
  }
  if (key === null || host === null) {
    throw new ConfigError('Set both VITE_WS_KEY and VITE_WS_HOST, or neither.')
  }
  if (!/^[a-z0-9.-]+$/i.test(host)) {
    throw new ConfigError('VITE_WS_HOST must be a host name, without scheme, port or path.')
  }
  return { key, host }
}

/**
 * Reads and validates the build-time environment once, at start-up. A missing
 * or malformed value stops the application before it renders anything, so a
 * misconfigured build fails on the first page load instead of on the first
 * API call.
 */
export function readConfig(env: AppEnv = import.meta.env): AppConfig {
  return {
    apiBaseUrl: readApiBaseUrl(env),
    glitchtipDsn: readVar(env, 'VITE_GLITCHTIP_DSN'),
    appVersion: requireVar(env, 'VITE_APP_VERSION'),
    liveUpdates: readLiveUpdates(env),
  }
}
